import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Play, Pause, Settings, Wind } from 'lucide-react';
import axios from 'axios';
import BreathingSettingsPanel from './BreathingSettingsPanel';
import useKeepAwake from '../hooks/useKeepAwake';
import KeepAwakeToggle from './KeepAwakeToggle';
import { useBreathingAudio } from '../hooks/useBreathingAudio';
import { useBreathingBackground } from '../hooks/useBreathingBackground';
import { recordSession } from '../utils/statsTracker';
import { unlockAudio } from '../utils/unlockAudio';
import { startBackgroundSession, stopBackgroundSession } from '../services/backgroundSession';
import AdInterstitial from './AdInterstitial';

const API = process.env.REACT_APP_BACKEND_URL;

// Programmes de respiration
const BREATHING_PROGRAMS = [
  {
    id: 'coherence', name: 'Cohérence cardiaque',
    description: '5-5 : Équilibre le système nerveux',
    phases: [
      { name: 'Inspirez', duration: 5, action: 'inhale' },
      { name: 'Expirez', duration: 5, action: 'exhale' }
    ],
    color: 'from-amber-400 to-cyan-500'
  },
  {
    id: 'relaxing', name: '4-7-8 Relaxant',
    description: 'Technique anti-stress profond',
    phases: [
      { name: 'Inspirez', duration: 4, action: 'inhale' },
      { name: 'Retenez', duration: 7, action: 'hold' },
      { name: 'Expirez', duration: 8, action: 'exhale' }
    ],
    color: 'from-purple-400 to-indigo-500'
  },
  {
    id: 'box', name: 'Respiration Équilibrée',
    description: '5-5-5-5 : Concentration et calme',
    phases: [
      { name: 'Inspirez', duration: 5, action: 'inhale' },
      { name: 'Retenez', duration: 5, action: 'hold' },
      { name: 'Expirez', duration: 5, action: 'exhale' },
      { name: 'Pause', duration: 5, action: 'pause' }
    ],
    color: 'from-amber-400 to-orange-500'
  },
  {
    id: 'energizing', name: 'Énergisant',
    description: '4-2-4 : Boost d\'énergie rapide',
    phases: [
      { name: 'Inspirez', duration: 4, action: 'inhale' },
      { name: 'Retenez', duration: 2, action: 'hold' },
      { name: 'Expirez', duration: 4, action: 'exhale' }
    ],
    color: 'from-rose-400 to-pink-500'
  }
];

export const BreathingMode = ({ onClose, isAdmin = false }) => {
  // UI state
  const [selectedProgram, setSelectedProgram] = useState(BREATHING_PROGRAMS[0]);
  const [isRunning, setIsRunning] = useState(false);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [phaseProgress, setPhaseProgress] = useState(0);
  const [cycleCount, setCycleCount] = useState(0);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [keepAwake, toggleKeepAwake] = useKeepAwake('breathing');
  const [totalDuration, setTotalDuration] = useState(5);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showInterstitial, setShowInterstitial] = useState(false);
  const [bgOpacity, setBgOpacity] = useState(20);
  const [menuTransparency, setMenuTransparency] = useState(40);

  const videoRef = useRef(null);
  const startTimeRef = useRef(null);
  const phaseStartTimeRef = useRef(null);
  const bgSessionRef = useRef(false);
  useEffect(() => () => {
    if (bgSessionRef.current) { bgSessionRef.current = false; stopBackgroundSession(); }
  }, []);
  const lastPhaseRef = useRef(null);

  // Custom hooks
  const audio = useBreathingAudio();
  const bg = useBreathingBackground();

  const currentPhase = selectedProgram.phases[currentPhaseIndex];

  // Fetch all data on mount
  useEffect(() => {
    audio.fetchAudioData();
    bg.fetchBackgroundData();
  }, []);

  // Animation du cercle
  const getCircleScale = useCallback(() => {
    if (!isRunning) return 1;
    const progress = phaseProgress / 100;
    switch (currentPhase.action) {
      case 'inhale': return 0.5 + (progress * 0.5);
      case 'exhale': return 1.0 - (progress * 0.5);
      case 'hold': case 'pause': return currentPhase.action === 'hold' ? 1.0 : 0.5;
      default: return 1;
    }
  }, [isRunning, phaseProgress, currentPhase]);

  // Refs for animation loop to avoid stale closures and effect re-runs
  const selectedProgramRef = useRef(selectedProgram);
  const currentPhaseIndexRef = useRef(currentPhaseIndex);
  const totalDurationRef = useRef(totalDuration);

  useEffect(() => { selectedProgramRef.current = selectedProgram; }, [selectedProgram]);
  useEffect(() => { currentPhaseIndexRef.current = currentPhaseIndex; }, [currentPhaseIndex]);
  useEffect(() => { totalDurationRef.current = totalDuration; }, [totalDuration]);

  // Animation loop - only depends on isRunning
  useEffect(() => {
    if (!isRunning) return;

    startTimeRef.current = Date.now();
    phaseStartTimeRef.current = Date.now();
    lastPhaseRef.current = null;

    const program = selectedProgramRef.current;
    let localPhaseIndex = 0;

    // Play initial sounds
    audio.playPhaseSound('start');
    audio.playPhaseSound(program.phases[0].action);
    audio.playVoiceGuidance(program.phases[0].action);
    lastPhaseRef.current = program.phases[0].action;

    const intervalId = setInterval(() => {
      const now = Date.now();
      const totalElapsed = (now - startTimeRef.current) / 1000;
      setElapsedTime(totalElapsed);

      const dur = totalDurationRef.current;
      if (totalElapsed >= dur * 60) {
        setIsRunning(false);
        recordSession('breathing', totalElapsed, selectedProgramRef.current.name);
        audio.playPhaseSound('end');
        setShowInterstitial(true);
        return;
      }

      const prog = selectedProgramRef.current;
      const phase = prog.phases[localPhaseIndex];
      const phaseElapsed = (now - phaseStartTimeRef.current) / 1000;
      const phaseDuration = phase.duration;
      const progress = Math.min((phaseElapsed / phaseDuration) * 100, 100);
      setPhaseProgress(progress);

      if (phaseElapsed >= phaseDuration) {
        const nextIndex = (localPhaseIndex + 1) % prog.phases.length;
        const nextPhase = prog.phases[nextIndex];
        audio.playPhaseSound(nextPhase.action);
        audio.playVoiceGuidance(nextPhase.action);
        lastPhaseRef.current = nextPhase.action;
        localPhaseIndex = nextIndex;
        setCurrentPhaseIndex(nextIndex);
        phaseStartTimeRef.current = now;
        if (nextIndex === 0) setCycleCount(prev => prev + 1);
      }
    }, 200);

    return () => clearInterval(intervalId);
  }, [isRunning]); // Only re-run when isRunning changes

  const handleStart = () => {
    // Unlock audio synchronously inside the user gesture so that voice
    // prompts triggered by the upcoming setInterval are not silenced in
    // in-app webviews (Instagram, Facebook, Messenger, TikTok…).
    unlockAudio();
    // Keep the session alive when the screen locks (foreground service on app)
    bgSessionRef.current = true;
    startBackgroundSession('Respiration guidée en cours');
    setCurrentPhaseIndex(0); setPhaseProgress(0);
    setCycleCount(0); setElapsedTime(0);
    startTimeRef.current = null; phaseStartTimeRef.current = null; lastPhaseRef.current = null;
    setIsRunning(true);
  };

  const handleStop = () => {
    if (isRunning && elapsedTime > 0) {
      recordSession('breathing', elapsedTime, selectedProgram.name);
    }
    setIsRunning(false);
    if (bgSessionRef.current) { bgSessionRef.current = false; stopBackgroundSession(); }
  };

  const handleProgramChange = (program) => {
    if (isRunning) handleStop();
    setSelectedProgram(program);
    selectedProgramRef.current = program;
    setCurrentPhaseIndex(0); setPhaseProgress(0); setCycleCount(0);
  };

  const handleClose = () => {
    audio.stopAllAudio();
    if (bgSessionRef.current) { bgSessionRef.current = false; stopBackgroundSession(); }
    onClose();
  };

  const formatElapsedTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const circleScale = getCircleScale();

  return (
    <div
      className="fixed inset-0 z-[200] mode-enter"
      style={{
        // Calming gradient fallback so the screen is never pitch-black while
        // the custom video/image background loads (or if the fetch fails).
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 45%, #0c4a6e 100%)',
      }}
      data-testid="breathing-mode"
    >
      {/* Background - Video ou Image */}
      <div className="absolute inset-0 overflow-hidden">
        {bg.isImageMode && bg.currentImage ? (
          <img
            key={bg.currentImage.id}
            src={bg.currentImage.url?.startsWith('http') ? bg.currentImage.url : `${API}${bg.currentImage.url}`}
            alt={bg.currentImage.name}
            className={`w-full h-full object-cover ${bg.getAnimationClass(bg.currentImage.animation)}`}
            style={{ filter: 'brightness(0.6) saturate(0.9)' }}
            fetchPriority="high"
            loading="eager"
          />
        ) : bg.currentBg ? (
          <video
            ref={videoRef}
            key={bg.currentBg?.id || 'default'}
            autoPlay loop muted playsInline preload="auto"
            className="w-full h-full object-cover animate-video-fade-in"
            style={{ filter: 'brightness(0.7) saturate(1.1)' }}
            onLoadedData={(e) => { e.target.style.opacity = '1'; }}
            src={bg.currentBg?.url?.startsWith('http') ? bg.currentBg.url : `${API}${bg.currentBg.url}`}
          />
        ) : null}
        <div className="absolute inset-0 bg-black transition-opacity" style={{ opacity: bgOpacity / 100 }} />
      </div>

      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 px-4 pt-10 pb-4 flex items-center justify-between z-10">
        <button onClick={handleClose}
          className="p-3 rounded-full backdrop-blur-sm text-white transition-all"
          style={{ backgroundColor: `rgba(0,0,0,${menuTransparency / 100})` }}
          data-testid="close-breathing">
          <X className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <KeepAwakeToggle keepAwake={keepAwake} onToggle={toggleKeepAwake} testId="keep-awake-breathing"
            className="px-3.5 py-2.5 rounded-full backdrop-blur-sm text-white text-xs font-medium hover:bg-white/10"
            style={{ backgroundColor: `rgba(0,0,0,${menuTransparency / 100})` }} />
          <button onClick={() => setShowSettingsPanel(!showSettingsPanel)}
            className={`px-5 py-2.5 rounded-full backdrop-blur-sm text-white text-sm font-medium transition-all flex items-center gap-2 ${
              showSettingsPanel ? 'bg-gradient-to-r from-amber-500/50 to-purple-500/50 border border-white/30' : 'hover:bg-white/10'
            }`}
            style={{ backgroundColor: showSettingsPanel ? undefined : `rgba(0,0,0,${menuTransparency / 100})` }}
            data-testid="settings-panel-btn">
            <Settings className="w-4 h-4" />
            <span className="whitespace-nowrap"><span className="hidden sm:inline">Paramétrer votre séance</span><span className="sm:hidden">Paramétrer</span></span>
          </button>
        </div>
      </div>

      {/* Main Content - Breathing Circle */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center max-w-xl mx-auto px-4">
          <div className="relative w-48 h-48 sm:w-60 sm:h-60 mx-auto mb-6">
            <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${selectedProgram.color} opacity-20 blur-3xl transition-transform duration-1000`}
              style={{ transform: `scale(${circleScale * 1.2})` }} />
            <div className={`absolute inset-3 rounded-full bg-gradient-to-br ${selectedProgram.color} opacity-30 transition-transform duration-1000 ease-in-out`}
              style={{ transform: `scale(${circleScale})` }} />
            <div className="absolute inset-6 rounded-full border-4 border-white/40 backdrop-blur-sm transition-transform duration-1000 ease-in-out flex items-center justify-center"
              style={{ transform: `scale(${circleScale})` }}>
              <div className="text-center">
                <Wind className={`w-8 h-8 mx-auto mb-1 text-white/80 transition-transform duration-500 ${
                  currentPhase.action === 'inhale' ? 'rotate-0' : currentPhase.action === 'exhale' ? 'rotate-180' : ''
                }`} />
                <p className="text-white text-xl sm:text-2xl font-heading font-semibold drop-shadow-lg">
                  {isRunning ? currentPhase.name : 'Prêt'}
                </p>
                {isRunning && (
                  <p className="text-white/70 text-base mt-0.5">
                    {Math.ceil(currentPhase.duration - (phaseProgress / 100 * currentPhase.duration))}s
                  </p>
                )}
              </div>
            </div>
            <svg className="absolute inset-0 w-full h-full -rotate-90">
              <circle cx="50%" cy="50%" r="45%" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
              <circle cx="50%" cy="50%" r="45%" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="4"
                strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 45}`}
                strokeDashoffset={`${2 * Math.PI * 45 * (1 - phaseProgress / 100)}`}
                className="transition-all duration-100" style={{ strokeDasharray: `${2 * Math.PI * 45}%` }} />
            </svg>
          </div>

          {/* Texte explicatif */}
          {!isRunning && (
            <div className="mb-6 animate-fade-in">
              <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-md mx-auto">
                La cohérence cardiaque synchronise votre coeur et votre esprit.
                <span className="text-amber-400 font-medium"> 5 minutes suffisent </span>
                pour réduire le stress, abaisser la tension artérielle et renforcer votre système immunitaire.
              </p>
              <p className="text-amber-300/80 text-xs sm:text-sm mt-2 font-medium">
                Pratiquez 3 fois par jour pour des résultats durables
              </p>
            </div>
          )}

          {/* Stats */}
          <div className="flex items-center justify-center gap-8 text-white/70">
            <div className="text-center">
              <p className="text-3xl font-heading text-white">{cycleCount}</p>
              <p className="text-sm">Cycles</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-heading text-white">{formatElapsedTime(elapsedTime)}</p>
              <p className="text-sm">Temps</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-heading text-white">{totalDuration}:00</p>
              <p className="text-sm">Objectif</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-0 left-0 right-0 p-6 flex flex-col items-center gap-3">
        <div className="flex items-center gap-2 bg-black/40 backdrop-blur-sm rounded-full px-4 py-2">
          <span className="text-white/60 text-sm">Durée:</span>
          {[3, 5, 10, 15].map((mins) => (
            <button key={mins} onClick={() => setTotalDuration(mins)}
              className={`px-3 py-1 rounded-full text-sm transition-all ${
                totalDuration === mins ? 'bg-white text-slate-900' : 'text-white/70 hover:text-white'
              }`}>
              {mins}min
            </button>
          ))}
        </div>
        {!isRunning ? (
          <button onClick={handleStart}
            className="px-8 py-4 rounded-full bg-white text-slate-900 font-semibold text-lg hover:scale-105 transition-transform shadow-xl flex items-center gap-2"
            data-testid="start-breathing">
            <Play className="w-6 h-6" /> Commencer
          </button>
        ) : (
          <button onClick={handleStop}
            className="px-8 py-4 rounded-full bg-white/20 backdrop-blur-sm text-white font-semibold text-lg hover:bg-white/30 transition-all flex items-center gap-2"
            data-testid="stop-breathing">
            <Pause className="w-6 h-6" /> Pause
          </button>
        )}
      </div>

      {/* Settings Panel */}
      <BreathingSettingsPanel
        isOpen={showSettingsPanel}
        onClose={() => setShowSettingsPanel(false)}
        selectedProgram={selectedProgram}
        onProgramChange={handleProgramChange}
        totalDuration={totalDuration}
        onDurationChange={setTotalDuration}
        bgOpacity={bgOpacity}
        onBgOpacityChange={setBgOpacity}
        menuTransparency={menuTransparency}
        onMenuTransparencyChange={setMenuTransparency}
        breathingMusic={audio.breathingMusic}
        selectedMusic={audio.selectedMusic}
        onMusicSelect={(m) => audio.playMusic(m)}
        musicVolume={audio.musicVolume}
        onMusicVolumeChange={(v) => audio.setMusicVolume(v)}
        ambientSounds={audio.ambientSounds}
        activeAmbients={audio.activeAmbients}
        onAmbientToggle={(s) => audio.toggleAmbient(s)}
        ambientVolumes={audio.ambientVolumes}
        onAmbientVolumeChange={(id, v) => audio.updateAmbientVolume(id, v)}
        phaseSoundsEnabled={audio.phaseSoundsEnabled}
        onPhaseSoundsToggle={() => audio.setPhaseSoundsEnabled(!audio.phaseSoundsEnabled)}
        phaseSoundVolume={audio.phaseSoundVolume}
        onPhaseSoundVolumeChange={audio.setPhaseSoundVolume}
        voiceGuidanceEnabled={audio.voiceGuidanceEnabled}
        onVoiceGuidanceToggle={() => audio.setVoiceGuidanceEnabled(!audio.voiceGuidanceEnabled)}
        selectedVoiceType={audio.selectedVoiceType}
        onVoiceTypeChange={audio.setSelectedVoiceType}
        coachGender={audio.coachGender}
        onCoachGenderChange={audio.changeCoachGender}
        maleVoices={audio.maleVoices}
        femaleCoachVoices={audio.femaleCoachVoices}
        femaleTahitianVoices={audio.femaleTahitianVoices}
        customVoices={audio.customVoices}
        isAdmin={isAdmin}
        lockedVoices={audio.lockedVoices}
        onToggleLockVoice={audio.toggleLockVoice}
        onDeleteVoice={audio.handleDeleteVoice}
        voiceVolume={audio.voiceVolume}
        onVoiceVolumeChange={audio.setVoiceVolume}
        onVoiceUpload={audio.handleVoiceUpload}
        uploadingVoice={audio.uploadingVoice}
        backgrounds={bg.customVideos}
        customVideos={bg.customVideos}
        selectedBg={bg.selectedBg}
        onBgSelect={(idx) => { bg.setSelectedBg(idx); bg.setSelectedImageBg(null); }}
        backgroundImages={bg.backgroundImages}
        selectedImageBg={bg.selectedImageBg}
        onImageBgSelect={bg.setSelectedImageBg}
        hoveredVideo={bg.hoveredVideo}
        onHoverVideo={bg.setHoveredVideo}
        onVideoUpload={bg.handleVideoUpload}
        onVideoRename={bg.handleVideoRename}
        onVideoToggleLock={bg.handleVideoToggleLock}
        onVideoDelete={bg.handleVideoDelete}
        defaultVideoId={bg.defaultVideoId}
        onSetDefaultVideo={bg.handleSetDefaultVideo}
        defaultImageId={bg.defaultImageId}
        onSetDefaultImage={bg.handleSetDefaultImage}
        onImageRename={bg.handleImageRename}
        onImageToggleLock={bg.handleImageToggleLock}
        onImageDelete={bg.handleImageDelete}
        uploadingVideo={bg.uploadingVideo}
        onImageUpload={async (file) => {
          bg.setUploadingImage(true);
          const formData = new FormData();
          formData.append('file', file);
          formData.append('name', file.name.replace(/\.[^/.]+$/, ''));
          formData.append('animation', bg.selectedAnimation);
          try {
            const response = await axios.post(API + '/api/breathing-images/upload', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            bg.backgroundImages.push(response.data);
          } catch (error) { console.error('Error uploading image:', error); }
          bg.setUploadingImage(false);
        }}
        uploadingImage={bg.uploadingImage}
        selectedAnimation={bg.selectedAnimation}
        onAnimationChange={bg.setSelectedAnimation}
      />
      <AdInterstitial show={showInterstitial} onClose={() => setShowInterstitial(false)} />
    </div>
  );
};

export default BreathingMode;
